import joinSrc from '../audios/Action_join.wav';
import leaveSrc from '../audios/Action_leave.wav';
import knockSrc from '../audios/Action_knockingdoor.wav';
import teaSrc from '../audios/herkesecay.mp3';
// kahve.mp3 / roket.mp3 ship as short silent placeholders - drop the real
// sounds in under the same names.
import coffeeSrc from '../audios/kahve.mp3';
import rocketSrc from '../audios/roket.mp3';
import callMeSrc from '../audios/makarayiz.mp3';

const SFX_VOLUME = 0.3;

// "Do not disturb" (SocketContext sets it from the user's status): no sound
// effects at all - joins/leaves, knocks, reaction sounds.
let muted = false;
export function setSoundEffectsMuted(value) {
    muted = !!value;
}

// One shared AudioContext for every sound effect - creating a fresh one per
// play would each need its own (slow) handshake with the OS audio device.
let audioContext = null;
function getAudioContext() {
    if (!audioContext) audioContext = new (window.AudioContext || window.webkitAudioContext)();
    return audioContext;
}

// Chromium suspends a freshly-created AudioContext until the page has had a
// user gesture (click, keydown, ...) - resume() is a harmless no-op once
// it's already running, so this is safe to call before every play.
function resumeIfSuspended(ctx) {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
}

// Fully decodes each file into an in-memory AudioBuffer once, up front at
// module load (i.e. app start) - NOT lazily on first play. This is the
// actual preloading: <audio>.preload='auto' is only ever a hint the browser
// is free to ignore or delay, and the previous implementation cloned a base
// <audio> element on every play - a clone carries over the source URL but
// not the already-decoded samples, so each one had to independently
// fetch/demux/decode from scratch, which is what made these sounds
// noticeably lag their trigger. Playing a decoded AudioBuffer through a
// fresh AudioBufferSourceNode has no per-play latency, and overlapping
// triggers (two people leaving within the same second) just get their own
// node instead of one cutting the other off.
function loadBuffer(src) {
    return fetch(src)
        .then((res) => res.arrayBuffer())
        .then((data) => getAudioContext().decodeAudioData(data));
}

function playBuffer(buffer, loop) {
    if (muted) return null;
    const ctx = getAudioContext();
    resumeIfSuspended(ctx);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    const gain = ctx.createGain();
    gain.gain.value = SFX_VOLUME;
    source.connect(gain).connect(ctx.destination);
    source.start();
    return source;
}

function createPlayer(src) {
    const bufferPromise = loadBuffer(src);
    return () => {
        // Rejection here (autoplay-policy denial, decode failure, ...)
        // isn't an error worth surfacing - the sound just doesn't play.
        bufferPromise.then((buffer) => playBuffer(buffer, false)).catch(() => {});
    };
}

// Ride along with their emoji reactions (see RoomReactions.js) - the
// sounds anybody in a room can trigger for everyone else.
export const playTeaSound = createPlayer(teaSrc);
export const playCoffeeSound = createPlayer(coffeeSrc);
export const playRocketSound = createPlayer(rocketSrc);
export const playCallMeSound = createPlayer(callMeSrc);

export const playJoinSound = createPlayer(joinSrc);
export const playLeaveSound = createPlayer(leaveSrc);

// Knock sound loops for as long as a given knock is outstanding (rather than
// one-shot) so it keeps ringing until it's resolved, same as a real
// doorbell. Keyed by knockId rather than a single shared instance because
// SocketContext calls start/stop from several independent event handlers
// (received/sent vs accepted/rejected/expired/cancelled) that only agree on
// the knockId - keying by it means whichever side's terminal event fires
// first correctly silences the loop without the two call sites needing to
// coordinate directly.
const knockBufferPromise = loadBuffer(knockSrc);
const knockLoops = new Map(); // knockId -> { cancelled, source? }

export function startKnockLoop(knockId) {
    if (knockLoops.has(knockId)) return;
    // Reserved immediately, before the (already-warm, but still async)
    // buffer promise resolves - so a stop that lands in that brief window
    // (a knock resolved almost instantly) has something to cancel instead
    // of racing the promise and starting a loop nothing will ever stop.
    const entry = { cancelled: false, source: null };
    knockLoops.set(knockId, entry);
    knockBufferPromise.then((buffer) => {
        if (entry.cancelled) return;
        entry.source = playBuffer(buffer, true);
    }).catch(() => {});
}

export function stopKnockLoop(knockId) {
    const entry = knockLoops.get(knockId);
    if (!entry) return;
    entry.cancelled = true;
    if (entry.source) entry.source.stop();
    knockLoops.delete(knockId);
}

// Safety net for the socket disconnecting (or SocketProvider unmounting)
// while a knock is still ringing - otherwise that loop plays forever with
// nothing left listening for its resolution.
export function stopAllKnockLoops() {
    knockLoops.forEach((entry) => {
        entry.cancelled = true;
        if (entry.source) entry.source.stop();
    });
    knockLoops.clear();
}
